
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isShopOpen, setIsShopOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileShopOpen, setIsMobileShopOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const [cartCount] = useState(3);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const isHome = location.pathname === '/';
  const isTransparent = !isScrolled && isHome;

  const navLinks = [
    { label: 'Home', to: '/' },
    { label: 'About Us', to: '/about' },
    { label: 'Grow It', to: '/grow' },
    { label: 'Blog', to: '/blog' },
  ];

  const shopItems = [
    { label: 'CBD Products', icon: 'ri-drop-line', to: '/shop' },
    { label: 'Seeds & Gear', icon: 'ri-seedling-line', to: '/shop' },
    { label: 'Wellness Products', icon: 'ri-heart-pulse-line', to: '/shop' },
    { label: 'All Products', icon: 'ri-store-2-line', to: '/shop' },
  ];

  const handleMobileLinkClick = () => {
    setIsMobileMenuOpen(false);
    setIsMobileShopOpen(false);
  };

  return (
    <>
      {/* Top Announcement Banner */}
      {showBanner && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-[#1A3A2A] text-white text-xs py-2 px-4 flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1 justify-center">
            <span className="flex items-center gap-1.5">
              <i className="ri-global-line text-[#D4AF37]"></i>
              <strong className="text-[#D4AF37]">elixirsue.com</strong>
              <span className="text-white/80">— International Store · All prices in</span>
              <strong className="text-[#D4AF37]">USD</strong>
            </span>
            <span className="hidden sm:flex items-center gap-1.5 text-white/60">
              |
            </span>
            <span className="hidden sm:flex items-center gap-1.5">
              <i className="ri-map-pin-line text-[#D4AF37]"></i>
              <span className="text-white/80">Canadian shoppers?</span>
              <a
                href="https://elixirsue.ca"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#D4AF37] font-semibold hover:underline cursor-pointer"
              >
                Visit elixirsue.ca →
              </a>
            </span>
          </div>
          <button
            onClick={() => setShowBanner(false)}
            className="ml-4 w-5 h-5 flex items-center justify-center text-white/60 hover:text-white transition-colors cursor-pointer flex-shrink-0"
            aria-label="Close banner"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>
      )}

      <nav
        className={`fixed left-0 right-0 z-50 transition-all duration-500 ${showBanner ? 'top-8' : 'top-0'} ${
          isTransparent
            ? 'bg-transparent py-2'
            : 'bg-white shadow-md py-0'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-20">

            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 cursor-pointer group z-50">
              <div className="w-12 h-12 flex items-center justify-center overflow-hidden rounded-full group-hover:scale-110 transition-transform duration-300">
                <img
                  src="https://static.readdy.ai/image/175fc0d3a7deb119f46e9d68342e64a7/3dea3d651ee128006b12d110f2ad0b27.png"
                  alt="Elixir Sue Logo"
                  className="h-12 w-12 object-contain"
                />
              </div>
              <div className="flex flex-col">
                <span
                  className={`text-2xl font-bold transition-colors duration-300 leading-tight ${
                    isTransparent ? 'text-white' : 'text-[#2D5F3F]'
                  }`}
                >
                  Elixir Sue
                </span>
                <span
                  className={`text-[10px] font-semibold tracking-widest uppercase transition-colors duration-300 ${
                    isTransparent ? 'text-[#D4AF37]/80' : 'text-[#D4AF37]'
                  }`}
                >
                  .com · International
                </span>
              </div>
            </Link>

            {/* Desktop Nav Links */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`relative text-base font-semibold transition-colors duration-300 cursor-pointer group ${
                    isTransparent ? 'text-white hover:text-[#D4AF37]' : 'text-[#2C2C2C] hover:text-[#2D5F3F]'
                  }`}
                >
                  {link.label}
                  <span
                    className={`absolute -bottom-1 left-0 h-0.5 bg-[#D4AF37] transition-all duration-300 ${
                      location.pathname === link.to ? 'w-full' : 'w-0 group-hover:w-full'
                    }`}
                  />
                </Link>
              ))}

              {/* Shop Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setIsShopOpen(true)}
                onMouseLeave={() => setIsShopOpen(false)}
              >
                <Link
                  to="/shop"
                  className={`relative text-base font-semibold transition-colors duration-300 cursor-pointer flex items-center gap-1 group ${
                    isTransparent ? 'text-white hover:text-[#D4AF37]' : 'text-[#2C2C2C] hover:text-[#2D5F3F]'
                  }`}
                >
                  Shop
                  <i
                    className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${
                      isShopOpen ? 'rotate-180' : ''
                    }`}
                  ></i>
                  <span
                    className={`absolute -bottom-1 left-0 h-0.5 bg-[#D4AF37] transition-all duration-300 ${
                      location.pathname === '/shop' ? 'w-full' : 'w-0 group-hover:w-full'
                    }`}
                  />
                </Link>

                {/* Dropdown */}
                <div
                  className={`absolute top-full left-0 mt-3 w-56 bg-white rounded-xl shadow-2xl py-2 border border-gray-100 transition-all duration-300 origin-top ${
                    isShopOpen
                      ? 'opacity-100 scale-y-100 translate-y-0 pointer-events-auto'
                      : 'opacity-0 scale-y-95 -translate-y-2 pointer-events-none'
                  }`}
                >
                  {shopItems.map((item) => (
                    <Link
                      key={item.label}
                      to={item.to}
                      className="flex items-center gap-3 px-5 py-3 text-sm font-medium text-[#2C2C2C] hover:bg-[#F8FAF9] hover:text-[#2D5F3F] hover:pl-7 transition-all duration-200 cursor-pointer group"
                    >
                      <i className={`${item.icon} text-[#D4AF37] text-base`}></i>
                      {item.label}
                      <i className="ri-arrow-right-s-line ml-auto opacity-0 group-hover:opacity-100 transition-opacity duration-200"></i>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Community */}
              <a
                href="#community"
                className={`relative text-base font-semibold transition-colors duration-300 cursor-pointer group ${
                  isTransparent ? 'text-white hover:text-[#D4AF37]' : 'text-[#2C2C2C] hover:text-[#2D5F3F]'
                }`}
              >
                Community
                <span className="absolute -bottom-1 left-0 h-0.5 bg-[#D4AF37] w-0 group-hover:w-full transition-all duration-300" />
              </a>

              {/* Cart */}
              <button
                className={`relative w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 hover:scale-110 cursor-pointer ${
                  isTransparent
                    ? 'text-white hover:bg-white/20'
                    : 'text-[#2C2C2C] hover:bg-[#2D5F3F] hover:text-white'
                }`}
              >
                <i className="ri-shopping-cart-line text-xl"></i>
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#D4AF37] text-white text-xs font-bold rounded-full flex items-center justify-center animate-[pop_0.3s_ease-out]">
                    {cartCount}
                  </span>
                )}
              </button>
            </div>

            {/* Mobile Menu Button & Cart */}
            <div className="flex lg:hidden items-center gap-4 z-50">
              <button
                className={`relative w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 cursor-pointer ${
                  isTransparent && !isMobileMenuOpen
                    ? 'text-white'
                    : 'text-[#2C2C2C]'
                }`}
              >
                <i className="ri-shopping-cart-line text-xl"></i>
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#D4AF37] text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>

              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`w-10 h-10 flex flex-col items-center justify-center gap-1.5 cursor-pointer transition-colors duration-300 ${
                  isTransparent && !isMobileMenuOpen
                    ? 'text-white'
                    : 'text-[#2C2C2C]'
                }`}
                aria-label="Toggle menu"
              >
                <span className={`w-6 h-0.5 bg-current transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                <span className={`w-6 h-0.5 bg-current transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : ''}`}></span>
                <span className={`w-6 h-0.5 bg-current transition-all duration-300 ${isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Drawer */}
        <div
          className={`fixed inset-0 bg-white z-40 lg:hidden transition-all duration-500 ${
            isMobileMenuOpen
              ? 'opacity-100 pointer-events-auto'
              : 'opacity-0 pointer-events-none'
          }`}
        >
          <div
            className={`h-full overflow-y-auto pt-24 pb-8 px-6 transition-transform duration-500 ${
              isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            {/* Mobile store indicator */}
            <div className="bg-[#1A3A2A]/10 rounded-lg px-4 py-3 mb-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-[#2D5F3F] uppercase tracking-wider">International Store (USD)</p>
                <p className="text-xs text-gray-500 mt-0.5">elixirsue.com</p>
              </div>
              <a
                href="https://elixirsue.ca"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#D4AF37] font-semibold hover:underline cursor-pointer whitespace-nowrap"
              >
                Switch to .ca (CAD) →
              </a>
            </div>

            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={handleMobileLinkClick}
                  className={`text-lg font-semibold py-4 px-4 rounded-lg transition-all duration-300 cursor-pointer ${
                    location.pathname === link.to
                      ? 'bg-[#2D5F3F] text-white'
                      : 'text-[#2C2C2C] hover:bg-[#F8FAF9] hover:text-[#2D5F3F]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}

              <div className="border-t border-gray-200 pt-2">
                <button
                  onClick={() => setIsMobileShopOpen(!isMobileShopOpen)}
                  className={`w-full text-lg font-semibold py-4 px-4 rounded-lg transition-all duration-300 cursor-pointer flex items-center justify-between ${
                    location.pathname === '/shop'
                      ? 'bg-[#2D5F3F] text-white'
                      : 'text-[#2C2C2C] hover:bg-[#F8FAF9] hover:text-[#2D5F3F]'
                  }`}
                >
                  Shop
                  <i className={`ri-arrow-down-s-line text-xl transition-transform duration-300 ${isMobileShopOpen ? 'rotate-180' : ''}`}></i>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${isMobileShopOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="pl-4 pt-2 flex flex-col gap-1">
                    {shopItems.map((item) => (
                      <Link
                        key={item.label}
                        to={item.to}
                        onClick={handleMobileLinkClick}
                        className="flex items-center gap-3 py-3 px-4 text-base font-medium text-[#2C2C2C] hover:bg-[#F8FAF9] hover:text-[#2D5F3F] rounded-lg transition-all duration-200 cursor-pointer"
                      >
                        <i className={`${item.icon} text-[#D4AF37] text-lg`}></i>
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              <a
                href="#community"
                onClick={handleMobileLinkClick}
                className="text-lg font-semibold py-4 px-4 rounded-lg text-[#2C2C2C] hover:bg-[#F8FAF9] hover:text-[#2D5F3F] transition-all duration-300 cursor-pointer"
              >
                Community
              </a>
            </div>

            <div className="mt-8 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-500 text-center mb-4">Join thousands worldwide</p>
              <div className="flex justify-center gap-4">
                <a href="#" className="w-12 h-12 rounded-full bg-[#2D5F3F] text-white flex items-center justify-center hover:bg-[#D4AF37] transition-all duration-300 cursor-pointer">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a href="#" className="w-12 h-12 rounded-full bg-[#2D5F3F] text-white flex items-center justify-center hover:bg-[#D4AF37] transition-all duration-300 cursor-pointer">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="#" className="w-12 h-12 rounded-full bg-[#2D5F3F] text-white flex items-center justify-center hover:bg-[#D4AF37] transition-all duration-300 cursor-pointer">
                  <i className="ri-pinterest-fill text-xl"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <style>{`
        @keyframes pop {
          0% { transform: scale(0); }
          70% { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
      `}</style>
    </>
  );
}
